import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';

// ─── Color / config maps ────────────────────────────────────────────────────
const STATUS_CONFIG = {
  Confirmed:    { badge: 'bg-green-100 text-green-700',  icon: 'CheckCircle2' },
  Pending:      { badge: 'bg-amber-100 text-amber-700',  icon: 'Clock' },
  Denied:       { badge: 'bg-red-100 text-red-700',      icon: 'XCircle' },
  'No Response':{ badge: 'bg-gray-100 text-gray-600',    icon: 'PhoneMissed' },
};

const TAG_LABELS = {
  'order-confirmed-by-ai-agent': 'Confirmed',
  'ai-agent-confirmation-pending': 'Pending',
  'ai-agent-could-not-connect-customer': 'Could Not Connect',
  'order-cancel-confirmed-by-ai-agent': 'Cancellation Confirmed',
};

const EVENT_ICON = {
  call:               { name: 'Phone',        bg: 'bg-blue-100',   color: '#2563eb' },
  tag_change:         { name: 'Tag',          bg: 'bg-blue-100',   color: '#3b82f6' },
  email_sent:         { name: 'Mail',         bg: 'bg-pink-100',   color: '#db2777' },
  callback_scheduled: { name: 'CalendarClock',bg: 'bg-teal-100',   color: '#0d9488' },
};

// ─── Audio Player ─────────────────────────────────────────────────────────────
const MiniAudioPlayer = ({ url }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => { if (audio.duration) setProgress((audio.currentTime / audio.duration) * 100); };
    const onEnd = () => { setPlaying(false); setProgress(0); };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    return () => { audio.removeEventListener('timeupdate', onTime); audio.removeEventListener('ended', onEnd); };
  }, []);

  return (
    <div className="mt-2 flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
      <audio ref={audioRef} src={url} preload="none" />
      <button onClick={toggle} className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 hover:bg-blue-700 transition-colors">
        <Icon name={playing ? 'Pause' : 'Play'} size={12} color="#fff" />
      </button>
      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-blue-500 rounded-full transition-all duration-100" style={{ width: `${progress}%` }} />
      </div>
      <Icon name="Headphones" size={13} color="#6b7280" />
    </div>
  );
};

// ─── Single Timeline Event ────────────────────────────────────────────────────
const TimelineEvent = ({ event }) => {
  const cfg = EVENT_ICON[event?.type] || EVENT_ICON.call;

  const renderContent = () => {
    switch (event?.type) {
      case 'call': {
        const isNoConnect = event.callStatus === 'no-answer' || event.callStatus === 'busy';
        const displayStatus = isNoConnect ? "Couldn't Connect" : event.callStatus;
        const outcomeColor =
          event.callStatus === 'completed' ? 'text-green-600' :
          event.callStatus === 'failed' || isNoConnect ? 'text-red-500' : 'text-gray-500';
        const duration = event.durationSeconds
          ? `${Math.floor(event.durationSeconds / 60)}m ${Math.round(event.durationSeconds % 60)}s`
          : null;
        const actionColor =
          event.postCallActionStatus === 'TRIGGERED' ? 'text-green-600 bg-green-50 border-green-200' :
          event.postCallActionStatus === 'CANCELLED' ? 'text-red-500 bg-red-50 border-red-200' :
          'text-blue-600 bg-blue-50 border-blue-200';
        return (
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-gray-800">Call</span>
              <span className={`text-xs font-medium ${outcomeColor}`}>· {displayStatus}</span>
              {duration && <span className="text-xs text-gray-400">· {duration}</span>}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{event.timestamp}</p>
            {event.callSummary && (
              <p className="text-xs text-gray-600 mt-1 bg-gray-50 rounded px-2 py-1 border border-gray-100 line-clamp-3">
                {event.callSummary}
              </p>
            )}
            {event.recordingUrl && event.recordingUrl !== '#' && <MiniAudioPlayer url={event.recordingUrl} />}
            {event.postCallAction && (
              <div className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${actionColor}`}>
                <Icon name="RotateCw" size={10} />
                {event.postCallAction}
              </div>
            )}
          </div>
        );
      }
      case 'tag_change': {
        const added = event.tagsAdded;
        const removed = event.tagsRemoved;
        const label = added ? (TAG_LABELS[added] || added) : null;
        const isUpdate = removed && added;
        return (
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">
                {isUpdate ? 'Shopify Tag Updated' : 'Shopify Tag Added'}
              </span>
              {label && (
                <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-blue-50 text-blue-600">
                  {label}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{event.timestamp}</p>
            {added && (
              <p className="text-xs text-gray-500 mt-0.5 font-mono">{added}</p>
            )}
          </div>
        );
      }
      case 'email_sent':
        return (
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">Email Sent</span>
              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-pink-50 text-pink-600">Sent</span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{event.timestamp}</p>
            {event.emailRecipient && (
              <p className="text-xs text-gray-600 mt-1">
                <span className="text-gray-400">To:</span> {event.emailRecipient}
              </p>
            )}
            {event.emailSubject && (
              <p className="text-xs text-gray-600 mt-0.5 bg-gray-50 rounded px-2 py-1 border border-gray-100 line-clamp-2">
                {event.emailSubject}
              </p>
            )}
          </div>
        );
      case 'callback_scheduled': {
        const statusColor =
          event.callbackStatus === 'TRIGGERED' ? 'bg-green-50 text-green-600' :
          event.callbackStatus === 'CANCELLED' ? 'bg-red-50 text-red-600' :
          'bg-teal-50 text-teal-600';
        const statusLabel =
          event.callbackStatus === 'TRIGGERED' ? 'Triggered' :
          event.callbackStatus === 'CANCELLED' ? 'Cancelled' : 'Pending';
        return (
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">Follow-up Call Scheduled</span>
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${statusColor}`}>{statusLabel}</span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{event.timestamp}</p>
            {event.scheduledAt && (
              <p className="text-xs text-teal-600 font-medium mt-1">
                <span className="text-gray-400">Scheduled for:</span> {event.scheduledAt}
              </p>
            )}
            {event.callbackReason && (
              <p className="text-xs text-gray-600 mt-0.5 bg-gray-50 rounded px-2 py-1 border border-gray-100 line-clamp-2">
                {event.callbackReason}
              </p>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="relative flex gap-3 pl-8">
      <div className={`absolute left-0 top-0.5 w-6 h-6 rounded-full ${cfg?.bg} flex items-center justify-center flex-shrink-0 z-10`}>
        <Icon name={cfg?.name} size={12} color={cfg?.color} />
      </div>
      <div className="flex-1 pb-4">{renderContent()}</div>
    </div>
  );
};

// ─── Main Drawer ──────────────────────────────────────────────────────────────
const OCDrawer = ({ order, onClose }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!order?.orderId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/api/v1/order-confirmation/orders/${order.orderId}/timeline`)
      .then((res) => { if (!cancelled) setTimeline(res.data?.data || []); })
      .catch((err) => console.error('OC timeline fetch failed:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [order?.orderId]);

  if (!order) return null;

  const statusCfg = STATUS_CONFIG[order?.confirmationStatus] || STATUS_CONFIG['Pending'];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
          <div>
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Order Timeline</p>
            <p className="text-base font-bold text-gray-900 font-mono">{order.orderId}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors">
            <Icon name="X" size={18} color="#6b7280" />
          </button>
        </div>

        {/* Order Info */}
        <div className="px-4 md:px-6 py-4 border-b border-gray-100 bg-white flex-shrink-0">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-400">Customer</p>
              <p className="text-sm font-semibold text-gray-800">{order.customerName || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Mobile</p>
              <p className="text-sm font-semibold text-blue-600">{order.phone || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Order Value</p>
              <p className="text-sm font-bold text-gray-900">{order.totalAmount ? `₹${order.totalAmount}` : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Status</p>
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${statusCfg?.badge}`}>
                <Icon name={statusCfg?.icon} size={11} />
                {order.confirmationStatus || 'Pending'}
              </span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-5">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">Activity Timeline</p>

          {loading ? (
            <div className="flex flex-col gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex gap-3 pl-8 animate-pulse">
                  <div className="w-6 h-6 rounded-full bg-gray-100" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-100 rounded w-40 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-64" />
                  </div>
                </div>
              ))}
            </div>
          ) : timeline.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">No activity recorded for this order.</p>
          ) : (
            <div className="relative">
              <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-100" />
              <div className="flex flex-col">
                {timeline.map((entry, idx) => (
                  <TimelineEvent key={idx} event={entry} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OCDrawer;
